'use client'

import { cn } from '@/lib/utils'

import {
  EASE,
  HAIRLINE,
  InlineLink,
  QuietLink,
  SectionHead,
  useReveal,
} from '@/components/page-kit'
import { HRMS } from './data'

/* The eight authored capability lines, verbatim, as a two-column ruled list.
   No checkmark icons: a tick in a circle beside each of eight short lines is
   the shape the shared template uses, and it reads as a marketing checklist.

   "What's included" is a structural label, not a claim — every claim on this
   page is a string from products.ts. */

export function HrmsIncluded() {
  const list = useReveal<HTMLDivElement>(0.1)

  return (
    <section id="included" className="scroll-mt-24 pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="Scope"
          title="What's included."
          action={
            <QuietLink href="/product-brochure">
              Download the brochure
            </QuietLink>
          }
        />

        <div
          ref={list.ref}
          className={cn(
            'mt-10 grid grid-cols-1 gap-x-12 border-t sm:mt-12 lg:grid-cols-2',
            HAIRLINE
          )}
        >
          {HRMS.capabilities.map((c, i) => (
            <div
              key={c}
              className={cn(
                'flex items-baseline gap-4 border-b py-4 sm:py-5',
                HAIRLINE
              )}
              style={{
                opacity: list.shown ? 1 : 0,
                transform: list.shown ? 'none' : 'translateY(8px)',
                transition: `opacity 460ms ${EASE} ${i * 60}ms, transform 460ms ${EASE} ${i * 60}ms`,
              }}
            >
              <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground/60">
                {String(i + 1).padStart(2, '0')}
              </span>
              <p className="font-heading text-base font-normal leading-snug text-foreground sm:text-lg">
                {c}
              </p>
            </div>
          ))}
        </div>

        {/* Same capability, read from the school's side of the platform. The
            module pages had no inbound link from the product they belong to. */}
        <p className="mt-8 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Running a school or college? The same records drive the{' '}
          <InlineLink href="/schoolerp/features/hrms">
            staff and workforce module
          </InlineLink>{' '}
          and the{' '}
          <InlineLink href="/schoolerp/features/payroll">
            payroll module
          </InlineLink>{' '}
          inside <InlineLink href="/schoolerp">Euclideum School ERP</InlineLink>
          .
        </p>
      </div>
    </section>
  )
}
